import React from 'react';
import { motion } from 'framer-motion';
import { X, ExternalLink } from 'lucide-react';
import Navbar from './Navbar';
import Footer from './Footer';

export default function ConnectStudents() {
  return (
    <div className="min-h-screen bg-[#0b1221] text-white overflow-hidden relative">
      <div className="relative z-50">
        <Navbar forceOpaque={true} />
      </div>

      <div className="relative pt-32 pb-20 px-6 lg:px-16 min-h-screen flex items-center">
        {/* Background glow effects */}
        <div className="absolute top-1/4 -left-32 w-96 h-96 bg-blue-600/30 rounded-full blur-[120px] pointer-events-none" />
        <div className="absolute bottom-1/4 right-0 w-[500px] h-[500px] bg-indigo-600/20 rounded-full blur-[150px] pointer-events-none" />
        
        <div className="max-w-7xl mx-auto w-full grid grid-cols-1 lg:grid-cols-2 gap-16 items-center z-10">
          
          {/* Left Content */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
          >
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-yellow-500/30 bg-yellow-500/10 text-yellow-400 text-sm font-semibold mb-8">
              <span className="w-2 h-2 rounded-full bg-yellow-400 animate-pulse"></span>
              AI-Powered Academic Excellence
            </div>
            
            <h1 className="text-5xl lg:text-7xl font-extrabold mb-6 font-syne leading-[1.15]">
              Elevate Your <br />
              <span className="text-yellow-500">Academic</span> Journey <br />
              with <span className="text-yellow-500">AI</span>
            </h1>

            <p className="text-slate-300 text-lg lg:text-xl mb-10 max-w-xl font-inter leading-relaxed">
              Premium projects, research papers & cutting-edge AI services for <span className="font-bold text-yellow-400">BE • BTech • MTech • BSc • Diploma • MCA • MBA • PhD</span> students across all technology domains.
            </p>

            <div className="flex flex-wrap gap-3 mb-12">
              {['AI & Machine Learning', 'IoT Systems', 'Cloud Computing', 'Research Papers', 'PhD Projects'].map((tag, i) => (
                <span key={i} className="px-4 py-2 rounded-full border border-slate-600 bg-slate-800/50 text-sm text-slate-300 font-inter">
                  {tag}
                </span>
              ))}
            </div>

            <div className="flex flex-wrap gap-4">
              <a href="https://www.connectstudents.in/" target="_blank" rel="noopener noreferrer">
                <button className="px-8 py-4 rounded-full bg-[#fde047] text-slate-900 font-bold font-syne hover:bg-yellow-400 transition-colors shadow-[0_0_20px_rgba(234,179,8,0.2)]">
                  Explore Services
                </button>
              </a>
              <a href="https://www.connectstudents.in/" target="_blank" rel="noopener noreferrer">
                <button className="px-8 py-4 rounded-full border border-slate-500 text-white hover:bg-slate-800 transition-colors font-syne font-semibold flex items-center gap-2">
                  Talk to Expert AI <ExternalLink size={18} />
                </button>
              </a>
            </div>
          </motion.div>

          {/* Right Mock Chat UI */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="relative hidden lg:block"
          >
            <div className="bg-white rounded-[2rem] shadow-2xl overflow-hidden border border-slate-700/50 transform rotate-2 hover:rotate-0 transition-transform duration-500">
              
              {/* Chat Header */}
              <div className="bg-[#1e293b] p-6 flex items-center justify-between border-b border-slate-700">
                <div className="flex items-center gap-4">
                  <div className="relative">
                    <div className="w-12 h-12 bg-[#fde047] rounded-full flex items-center justify-center text-slate-900 font-bold">
                      CS
                    </div>
                    <div className="absolute bottom-0 right-0 w-3.5 h-3.5 bg-emerald-400 rounded-full border-2 border-[#1e293b]"></div>
                  </div>
                  <div>
                    <h3 className="text-white font-bold font-syne text-lg">CS Expert AI</h3>
                    <p className="text-emerald-400 text-sm font-inter">Online • Powered by Groq AI</p>
                  </div>
                </div>
                <button className="w-8 h-8 rounded-full bg-slate-700 flex items-center justify-center text-slate-300 hover:bg-slate-600">
                  <X size={16} />
                </button>
              </div>

              {/* Chat Body */}
              <div className="p-8 bg-[#fafafa] h-[480px] flex flex-col justify-between">
                <div className="flex gap-4">
                  <div className="w-8 h-8 rounded-full bg-[#fde047] flex-shrink-0 flex items-center justify-center text-slate-900 text-xs mt-1">
                    ✨
                  </div>
                  <div className="bg-white p-5 rounded-2xl rounded-tl-none border border-slate-200 shadow-sm text-slate-700 font-inter text-sm leading-relaxed max-w-[90%]">
                    Hello! 👋 Welcome to <span className="font-bold text-slate-900">Connect Students AI Assistant</span>.
                    <br/><br/>
                    I'm here to help you find the perfect academic project, suggest ideas, and generate a detailed project report — all powered by AI!
                    <br/><br/>
                    <span className="text-slate-500">What would you like to do?</span>
                  </div>
                </div>

                <div className="space-y-3 mt-8">
                  {[
                    'Talk to Expert — Get My Project',
                    'Browse Projects by Domain',
                    'Know Pricing & Delivery'
                  ].map((option, i) => (
                    <button key={i} className="w-full text-left p-4 rounded-xl border border-[#fde047]/60 hover:border-[#fde047] hover:bg-yellow-50 transition-colors flex items-center gap-3 text-slate-700 font-semibold text-sm">
                      <span className="w-2 h-2 rounded-full bg-[#fde047]"></span>
                      {option}
                    </button>
                  ))}
                </div>
                <p className="text-slate-400 text-xs mt-4">01:27 pm</p>
              </div>
            </div>
            
            <div className="absolute -top-6 -right-6 w-24 h-24 bg-[radial-gradient(circle,rgba(253,224,71,0.2)_2px,transparent_2px)] bg-[length:12px_12px] opacity-60"></div>
          </motion.div>
        </div>
      </div>

      <div className="bg-white">
        <Footer />
      </div>
    </div>
  );
}
